import React from 'react';
import { KpiCard, toneColor } from '@/design-system/dna';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Users, GraduationCap, TrendingUp, TrendingDown, AlertTriangle,
  BookOpen, Clock, Award, Target, Activity, Brain, Sparkles,
  BarChart3, PieChart, ArrowUp, ArrowDown, CheckCircle, XCircle
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

// Cores premium
const COLORS = {
  purple: '#8E52AB',
  green: '#10B981',
  blue: '#3B82F6',
  orange: '#F59E0B',
  red: '#EF4444',
  pink: '#EC4899'
};

const CLASSIFICATION_COLORS = {
  excelente: '#10B981',
  estavel: '#3B82F6',
  atencao: '#F59E0B',
  critico: '#EF4444'
};

// Componente de Card KPI
function KPICard({ title, value, subtitle, icon, color, trendValue }) {
  return <KpiCard id={`pg-${String(title).toLowerCase().replace(/\s+/g, '-')}`} icon={icon} label={title} value={value} hint={[subtitle, trendValue].filter(Boolean).join(' · ')} color={toneColor(color)} />;
}

// Componente de Score Circular Premium
function ScoreCircle({ score, size = 'lg', label }) {
  const sizeMap = {
    sm: { container: 'w-16 h-16', text: 'text-xl', ring: 2 },
    md: { container: 'w-24 h-24', text: 'text-3xl', ring: 3 },
    lg: { container: 'w-32 h-32', text: 'text-4xl', ring: 4 }
  };
  
  const getColor = (s) => {
    if (s >= 80) return 'from-green-500 to-green-400';
    if (s >= 60) return 'from-blue-500 to-blue-400';
    if (s >= 40) return 'from-orange-500 to-orange-400';
    return 'from-red-500 to-red-400';
  };
  
  const percentage = Math.min(Math.max(score, 0), 100);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
    <div className="flex flex-col items-center gap-2" data-testid="score-circle">
      <div className={`${sizeMap[size].container} relative`}>
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="50%"
            cy="50%"
            r="45%"
            fill="none"
            stroke="rgba(139, 92, 246, 0.2)"
            strokeWidth={sizeMap[size].ring}
          />
          <circle
            cx="50%"
            cy="50%"
            r="45%"
            fill="none"
            stroke="url(#scoreGradient)"
            strokeWidth={sizeMap[size].ring}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
          <defs>
            <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={COLORS.purple} />
              <stop offset="100%" stopColor={COLORS.green} />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`${sizeMap[size].text} font-bold text-white`}>
            {Math.round(score)}
          </span>
        </div>
      </div>
      {label && <span className="text-sm text-purple-300/70 font-medium">{label}</span>}
    </div>
  );
}

// Gráfico de Distribuição de Classificação
function ClassificacaoChart({ data }) {
  const chartData = [
    { name: 'Excelente', value: data?.excelente || 0, color: CLASSIFICATION_COLORS.excelente },
    { name: 'Estável', value: data?.estavel || 0, color: CLASSIFICATION_COLORS.estavel },
    { name: 'Atenção', value: data?.atencao || 0, color: CLASSIFICATION_COLORS.atencao },
    { name: 'Crítico', value: data?.critico || 0, color: CLASSIFICATION_COLORS.critico }
  ].filter(d => d.value > 0);
  
  const total = chartData.reduce((acc, d) => acc + d.value, 0);
  
  return (
    <Card className="bg-slate-800/50 border-purple-500/20" data-testid="classificacao-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <PieChart className="w-5 h-5 text-purple-400" />
          Distribuição por Classificação
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-6">
          <div className="w-40 h-40">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPie>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1E293B', 
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-3">
            {chartData.map((item) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-purple-200 text-sm">{item.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold">{item.value}</span>
                  <span className="text-purple-300/50 text-xs">
                    ({total > 0 ? Math.round((item.value / total) * 100) : 0}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Gráfico de Evolução Mensal
function EvolucaoChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <Card className="bg-slate-800/50 border-purple-500/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <TrendingUp className="w-5 h-5 text-purple-400" />
            Evolução Mensal
          </CardTitle>
        </CardHeader>
        <CardContent className="h-64 flex items-center justify-center">
          <p className="text-purple-300/50">Sem dados de evolução</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="bg-slate-800/50 border-purple-500/20" data-testid="evolucao-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <TrendingUp className="w-5 h-5 text-purple-400" />
          Evolução de Médias
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorMedia" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.purple} stopOpacity={0.4}/>
                  <stop offset="95%" stopColor={COLORS.purple} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(139, 92, 246, 0.1)" />
              <XAxis 
                dataKey="mes" 
                stroke="rgba(139, 92, 246, 0.5)"
                tick={{ fill: 'rgba(139, 92, 246, 0.7)', fontSize: 12 }}
              />
              <YAxis 
                stroke="rgba(139, 92, 246, 0.5)"
                tick={{ fill: 'rgba(139, 92, 246, 0.7)', fontSize: 12 }}
                domain={[0, 10]}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1E293B', 
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
                formatter={(value) => [value.toFixed(2), 'Média']}
              />
              <Area 
                type="monotone" 
                dataKey="media" 
                stroke={COLORS.purple} 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorMedia)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Gráfico de Distribuição de Notas
function DistribuicaoNotasChart({ data }) {
  const chartData = data?.contagem ? [
    { faixa: '0-2', quantidade: data.contagem['0-2'] || 0, color: COLORS.red },
    { faixa: '2-4', quantidade: data.contagem['2-4'] || 0, color: COLORS.orange },
    { faixa: '4-6', quantidade: data.contagem['4-6'] || 0, color: '#F59E0B' },
    { faixa: '6-8', quantidade: data.contagem['6-8'] || 0, color: COLORS.blue },
    { faixa: '8-10', quantidade: data.contagem['8-10'] || 0, color: COLORS.green }
  ] : [];
  
  return (
    <Card className="bg-slate-800/50 border-purple-500/20" data-testid="distribuicao-notas-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <BarChart3 className="w-5 h-5 text-purple-400" />
          Distribuição de Notas
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(139, 92, 246, 0.1)" />
              <XAxis 
                dataKey="faixa" 
                stroke="rgba(139, 92, 246, 0.5)"
                tick={{ fill: 'rgba(139, 92, 246, 0.7)', fontSize: 12 }}
              />
              <YAxis 
                stroke="rgba(139, 92, 246, 0.5)"
                tick={{ fill: 'rgba(139, 92, 246, 0.7)', fontSize: 12 }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1E293B', 
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
                formatter={(value) => [value, 'Notas']}
              />
              <Bar dataKey="quantidade" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Ranking de Turmas
function TurmasRanking({ turmas }) {
  if (!turmas || turmas.length === 0) {
    return (
      <Card className="bg-slate-800/50 border-purple-500/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <Award className="w-5 h-5 text-purple-400" />
            Ranking de Turmas
          </CardTitle>
        </CardHeader>
        <CardContent className="h-64 flex items-center justify-center">
          <p className="text-purple-300/50">Sem dados de turmas</p>
        </CardContent>
      </Card>
    );
  }
  
  const getMedalColor = (pos) => {
    if (pos === 1) return 'bg-yellow-500';
    if (pos === 2) return 'bg-gray-400';
    if (pos === 3) return 'bg-orange-500';
    return 'bg-slate-600';
  };
  
  return (
    <Card className="bg-slate-800/50 border-purple-500/20" data-testid="turmas-ranking">
      <CardHeader className="pb-2">
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <Award className="w-5 h-5 text-purple-400" />
          Ranking de Turmas
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-72 overflow-y-auto pr-2 custom-scrollbar">
          {turmas.map((turma, idx) => (
            <div 
              key={turma.turma_id}
              className="flex items-center gap-3 p-3 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors"
            >
              <div className={`w-7 h-7 rounded-full ${getMedalColor(turma.posicao)} flex items-center justify-center text-white text-sm font-bold`}>
                {turma.posicao}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{turma.turma_nome}</p>
                <p className="text-purple-300/50 text-xs">{turma.total_alunos} alunos</p>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2">
                  <div className={`px-2 py-1 rounded text-white text-sm font-bold ${
                    turma.score_medio >= 80 ? 'bg-green-500' :
                    turma.score_medio >= 60 ? 'bg-blue-500' :
                    turma.score_medio >= 40 ? 'bg-orange-500' : 'bg-red-500'
                  }`}>
                    {turma.score_medio.toFixed(1)}
                  </div>
                </div>
                {turma.criticos > 0 && (
                  <p className="text-red-400 text-xs mt-1">{turma.criticos} críticos</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Lista de Alunos em Risco
function AlunosRiscoList({ alunos }) {
  if (!alunos || alunos.length === 0) {
    return (
      <Card className="bg-slate-800/50 border-purple-500/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <AlertTriangle className="w-5 h-5 text-orange-400" />
            Alunos em Risco
          </CardTitle>
        </CardHeader>
        <CardContent className="h-64 flex items-center justify-center">
          <div className="text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-2" />
            <p className="text-purple-300/70">Nenhum aluno em situação crítica</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const getRiscoColor = (risco) => {
    if (risco === 'critico') return 'bg-red-500';
    if (risco === 'alto') return 'bg-orange-500';
    if (risco === 'medio') return 'bg-yellow-500';
    return 'bg-green-500';
  };
  
  return (
    <Card className="bg-slate-800/50 border-purple-500/20" data-testid="alunos-risco-list">
      <CardHeader className="pb-2">
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <AlertTriangle className="w-5 h-5 text-orange-400" />
          Alunos em Risco
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
            {alunos.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-72 overflow-y-auto pr-2 custom-scrollbar">
          {alunos.map((aluno) => (
            <div 
              key={aluno.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors cursor-pointer"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-purple-400 flex items-center justify-center text-white font-bold">
                {aluno.nome?.charAt(0) || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{aluno.nome}</p>
                <p className="text-purple-300/50 text-xs">{aluno.turma}</p>
              </div>
              <div className="text-right flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full ${
                  aluno.score_total < 40 ? 'bg-red-500' :
                  aluno.score_total < 60 ? 'bg-orange-500' : 'bg-yellow-500'
                } flex items-center justify-center text-white text-xs font-bold`}>
                  {Math.round(aluno.score_total)}
                </div>
                <Badge className={`${getRiscoColor(aluno.risco_reprovacao)} text-white text-xs`}>
                  {aluno.risco_reprovacao}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Componente Principal do Painel Global
export default function PainelGlobal({ data, turmas }) {
  const kpis = data?.kpis || {};
  
  return (
    <div className="space-y-6" data-testid="painel-global">
      {/* Hero Section com Score Global */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="lg:col-span-1 bg-gradient-to-br from-purple-900/50 to-slate-800/50 border-purple-500/30">
          <CardContent className="pt-6 pb-6 flex flex-col items-center justify-center h-full">
            <div className="relative">
              <ScoreCircle score={kpis.score_dnacore?.media || 0} size="lg" />
              <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-yellow-400 animate-pulse" />
            </div>
            <h3 className="text-lg font-semibold text-white mt-4">Score DNACore</h3>
            <p className="text-purple-300/70 text-sm text-center">
              Saúde Pedagógica Global
            </p>
          </CardContent>
        </Card>
        
        <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPICard
            title="Total de Alunos"
            value={kpis.alunos?.total || 0}
            subtitle={`${kpis.alunos?.turmas || 0} turmas`}
            icon={Users}
            color="purple"
          />
          <KPICard
            title="Média Geral"
            value={(kpis.notas?.media_geral || 0).toFixed(1)}
            subtitle={`${kpis.notas?.total || 0} notas`}
            icon={BookOpen}
            color="blue"
          />
          <KPICard
            title="Frequência"
            value={`${kpis.frequencia?.taxa || 0}%`}
            subtitle={`${kpis.frequencia?.total_faltas || 0} faltas`}
            icon={Clock}
            color="green"
          />
          <KPICard
            title="Em Risco"
            value={kpis.risco?.alunos_risco_alto || 0}
            subtitle={`${kpis.risco?.alertas_ativos || 0} alertas`}
            icon={AlertTriangle}
            color="red"
          />
        </div>
      </div>
      
      {/* Scores por Dimensão */}
      <Card className="bg-slate-800/50 border-purple-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <Brain className="w-5 h-5 text-purple-400" />
            Scores por Dimensão
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: 'Acadêmico', value: kpis.score_dnacore?.academico || 0, icon: BookOpen, color: 'blue' },
              { label: 'Frequência', value: kpis.score_dnacore?.frequencia || 0, icon: Clock, color: 'green' },
              { label: 'Comportamental', value: kpis.score_dnacore?.comportamental || 0, icon: Activity, color: 'purple' },
              { label: 'Geral', value: kpis.score_dnacore?.media || 0, icon: Target, color: 'pink' }
            ].map((item) => (
              <div key={item.label} className="text-center">
                <div className={`w-16 h-16 mx-auto rounded-full bg-gradient-to-br ${
                  item.color === 'blue' ? 'from-blue-600 to-blue-400' :
                  item.color === 'green' ? 'from-green-600 to-green-400' :
                  item.color === 'purple' ? 'from-purple-600 to-purple-400' :
                  'from-pink-600 to-pink-400'
                } flex items-center justify-center text-white text-xl font-bold shadow-lg`}>
                  {Math.round(item.value)}
                </div>
                <p className="text-purple-200 mt-2 text-sm font-medium">{item.label}</p>
                <div className="mt-2">
                  <Progress 
                    value={item.value} 
                    className="h-1.5 bg-slate-700"
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Grid de Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ClassificacaoChart data={data?.kpis?.classificacao} />
        <EvolucaoChart data={data?.evolucao_mensal} />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <DistribuicaoNotasChart data={data?.distribuicao_notas} />
        <TurmasRanking turmas={data?.turmas_ranking} />
        <AlunosRiscoList alunos={data?.alunos_risco} />
      </div>
      
      {/* Estilos CSS para scrollbar customizado */}
      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(139, 92, 246, 0.1);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(139, 92, 246, 0.3);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(139, 92, 246, 0.5);
        }
      `}</style>
    </div>
  );
}
